import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import { useToast } from '../components/shared/ToastNotification';

export default function OrderFinanceManagement({ orderIdProp, inModal = false, onSuccess }: { orderIdProp?: string, inModal?: boolean, onSuccess?: () => void }) {
    const params = useParams();
    const id = orderIdProp || params.id;
    const toast = useToast();
    const [order, setOrder] = useState<any>(null);
    const [finances, setFinances] = useState<FinanceRecord[]>([]);
    const [financeCompanies, setFinanceCompanies] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingFinance, setEditingFinance] = useState<FinanceRecord | null>(null);
    const [formData, setFormData] = useState({
        product_unit_id: '',
        finance_company_id: '',
        on_road_value: '',
        processing_charge: '',
        down_payment: '',
        year_of_finance: '',
        rate_of_interest: '',
        installment_amount: '',
        number_of_installments: '',
        emi_start_date: '',
        loan_account_number: '',
        finance_status: 'pending'
    });

    useEffect(() => {
        loadData();
    }, [id]);

    // Handle direct add for modal view
    useEffect(() => {
        if (inModal && finances.length === 0 && !showForm && !editingFinance) {
            handleAdd();
        }
    }, [inModal, finances.length, showForm, editingFinance]);

    const loadData = async () => {
        try {
            const [orderRes, financesRes, companiesRes] = await Promise.all([
                api.get(`/orders/${id}`),
                api.get(`/orders/${id}/finances`),
                api.get('/finance_companies')
            ]);
            setOrder(orderRes.data);
            const financesData = Array.isArray(financesRes.data) ? financesRes.data : (financesRes.data.data || []);
            setFinances(financesData);
            setFinanceCompanies(companiesRes.data.data || companiesRes.data || []);
        } catch (error) {
            console.error('Error loading data:', error);
            toast.error('Failed to load finance data');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setShowForm(false);
        setEditingFinance(null);
        setFormData({
            product_unit_id: '',
            finance_company_id: '',
            on_road_value: '',
            processing_charge: '',
            down_payment: '',
            year_of_finance: '',
            rate_of_interest: '',
            installment_amount: '',
            number_of_installments: '',
            emi_start_date: '',
            loan_account_number: '',
            finance_status: 'pending'
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            let response;
            if (editingFinance) {
                response = await api.put(`/orders/${id}/finances/${editingFinance.id}`, { finance: formData });
            } else {
                response = await api.post(`/orders/${id}/finances`, formData);
            }

            if (response.success) {
                toast.success(`Finance record ${editingFinance ? 'updated' : 'created'} successfully`);
                loadData();
                resetForm();
                if (onSuccess) onSuccess();
            } else {
                const msg = response.errors?.length > 0 ? response.errors[0] : (response.message || response.error || 'Error saving finance record');
                toast.error(msg);
            }
        } catch (error: any) {
            console.error('Error saving finance:', error);
            toast.error(error.message || 'An unexpected error occurred');
        }
    };

    const handleEdit = (finance: FinanceRecord) => {
        setEditingFinance(finance);
        setFormData({
            product_unit_id: finance.product_unit_id,
            finance_company_id: finance.finance_company_id || '',
            on_road_value: '',
            processing_charge: '',
            down_payment: finance.down_payment?.toString() || '',
            year_of_finance: finance.year_of_finance?.toString() || '',
            rate_of_interest: finance.rate_of_interest?.toString() || '',
            installment_amount: finance.installment_amount?.toString() || '',
            number_of_installments: '',
            emi_start_date: '',
            loan_account_number: '',
            finance_status: finance.finance_status
        });
        setShowForm(true);
    };

    const handleAdd = () => {
        setEditingFinance(null);
        setFormData({
            product_unit_id: order?.items?.length === 1 ? (order.items[0].product_unit_id || order.items[0].id) : '',
            finance_company_id: '',
            on_road_value: '',
            processing_charge: '',
            down_payment: '',
            year_of_finance: '',
            rate_of_interest: '',
            installment_amount: '',
            number_of_installments: '',
            emi_start_date: '',
            loan_account_number: '',
            finance_status: 'pending'
        });
        setShowForm(true);
    };

    const handleDelete = async (financeId: string) => {
        if (!confirm('Are you sure you want to delete this finance record?')) return;
        try {
            await api.delete(`/orders/${id}/finances/${financeId}`);
            loadData();
            if (onSuccess) onSuccess();
        } catch (error) {
            console.error('Error deleting finance:', error);
        }
    };


    const getStatusBadge = (status: string) => {
        const badges: Record<string, string> = {
            pending: 'bg-gray-100 text-gray-700',
            approved: 'bg-green-100 text-green-700',
            disbursed: 'bg-blue-100 text-blue-700',
            closed: 'bg-slate-100 text-slate-700',
            rejected: 'bg-red-100 text-red-700'
        };
        return badges[status] || badges.pending;
    };

    if (loading) return <div className="p-8">Loading...</div>;
    if (!order) return <div className="p-8">Order not found.</div>;

    return (
        <div className="p-8 max-w-6xl mx-auto">
            <div className="mb-8">
                {!inModal && (
                    <Link to={`/orders/${id}`} className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-800 font-bold mb-4">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                        </svg>
                        Back to Order
                    </Link>
                )}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className={`${inModal ? 'text-2xl' : 'text-3xl'} font-black text-slate-900`}>Finance Management</h1>
                        {!inModal && <p className="text-slate-600 mt-2">Order #{order.order_number}</p>}
                    </div>
                    <button
                        onClick={handleAdd}
                        className="px-6 py-3 bg-amber-600 text-white rounded-xl font-bold hover:bg-amber-700 transition-all text-sm"
                    >
                        + Add Finance
                    </button>
                </div>
            </div>

            {/* Finance Records Table */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8">
                <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                            <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase">Product</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase">Finance Company</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase">Status</th>
                            <th className="px-6 py-4 text-right text-xs font-bold text-slate-600 uppercase">Loan Amount</th>
                            <th className="px-6 py-4 text-right text-xs font-bold text-slate-600 uppercase">EMI</th>
                            <th className="px-6 py-4 text-right text-xs font-bold text-slate-600 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {finances.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                                    No finance records found. Click "Add Finance" to create one.
                                </td>
                            </tr>
                        ) : (
                            finances.map((fin) => (
                                <tr key={fin.id} className="hover:bg-slate-50">
                                    <td className="px-6 py-4 font-medium text-slate-900">{fin.product_name || 'Unknown'}</td>
                                    <td className="px-6 py-4 text-slate-700">{fin.finance_company_name || '-'}</td>
                                    <td className="px-6 py-4">
                                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusBadge(fin.finance_status)}`}>
                                            {fin.finance_status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-right font-mono text-slate-900">
                                        {fin.finance_amount ? `₹${fin.finance_amount.toLocaleString()}` : '-'}
                                    </td>
                                    <td className="px-6 py-4 text-right font-mono text-slate-900">
                                        {fin.installment_amount ? `₹${fin.installment_amount.toLocaleString()}` : '-'}
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <button
                                            onClick={() => handleEdit(fin)}
                                            className="text-indigo-600 hover:text-indigo-800 font-bold mr-4"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDelete(fin.id)}
                                            className="text-red-600 hover:text-red-800 font-bold"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Form Section */}
            {showForm && (
                <div className={inModal ? "mb-8 bg-slate-50 rounded-2xl p-8 border border-slate-200" : "fixed inset-0 bg-black/50 flex items-center justify-center z-50"}>
                    <div className={inModal ? "" : "bg-white rounded-2xl p-8 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto"}>
                        <h2 className="text-2xl font-black text-slate-900 mb-6">
                            {editingFinance ? 'Edit Finance' : 'Add Finance'}
                        </h2>
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {!editingFinance && (
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Product</label>
                                    {order.items?.length === 1 ? (
                                        <div className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-slate-700 font-medium">
                                            {order.items[0].product_name} - {order.items[0].product_unit_identifier || 'No SN'}
                                        </div>
                                    ) : (
                                        <select
                                            required
                                            value={formData.product_unit_id}
                                            onChange={(e) => setFormData({ ...formData, product_unit_id: e.target.value })}
                                            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                        >
                                            <option value="">Select Product</option>
                                            {order.items?.map((item: any) => (
                                                <option key={item.id} value={item.product_unit_id || item.id}>
                                                    {item.product_name} - {item.product_unit_identifier || 'No SN'}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">Finance Company</label>
                                <select
                                    value={formData.finance_company_id}
                                    onChange={(e) => setFormData({ ...formData, finance_company_id: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                >
                                    <option value="">Select Finance Company</option>
                                    {financeCompanies.map((company) => (
                                        <option key={company.id} value={company.id}>{company.company_name}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">Finance Status</label>
                                <select
                                    value={formData.finance_status}
                                    onChange={(e) => setFormData({ ...formData, finance_status: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                >
                                    <option value="pending">Pending</option>
                                    <option value="approved">Approved</option>
                                    <option value="disbursed">Disbursed</option>
                                    <option value="closed">Closed</option>
                                    <option value="rejected">Rejected</option>
                                </select>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">On-Road Value</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={formData.on_road_value}
                                        onChange={(e) => setFormData({ ...formData, on_road_value: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                        placeholder="0.00"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Down Payment</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={formData.down_payment}
                                        onChange={(e) => setFormData({ ...formData, down_payment: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                        placeholder="0.00"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Tenure (Years)</label>
                                    <input
                                        type="number"
                                        value={formData.year_of_finance}
                                        onChange={(e) => setFormData({ ...formData, year_of_finance: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                        placeholder="1"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Interest Rate (%)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={formData.rate_of_interest}
                                        onChange={(e) => setFormData({ ...formData, rate_of_interest: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                        placeholder="12.00"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">EMI Amount</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={formData.installment_amount}
                                        onChange={(e) => setFormData({ ...formData, installment_amount: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                        placeholder="0.00"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">EMI Start Date</label>
                                    <input
                                        type="date"
                                        value={formData.emi_start_date}
                                        onChange={(e) => setFormData({ ...formData, emi_start_date: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Loan Account Number</label>
                                    <input
                                        type="text"
                                        value={formData.loan_account_number}
                                        onChange={(e) => setFormData({ ...formData, loan_account_number: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                        placeholder="LOAN123456"
                                    />
                                </div>
                            </div>

                            <div className="flex gap-4 pt-4">
                                <button
                                    type="submit"
                                    className="flex-1 px-6 py-3 bg-amber-600 text-white rounded-xl font-bold hover:bg-amber-700 transition-all"
                                >
                                    {editingFinance ? 'Update' : 'Create'} Finance Record
                                </button>
                                <button
                                    type="button"
                                    onClick={resetForm}
                                    className="px-6 py-3 bg-slate-200 text-slate-700 rounded-xl font-bold hover:bg-slate-300 transition-all"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
